import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalClose,
  Sheet,
  Typography,
} from "@mui/joy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";

const CreateApiKeyModal = ({ open, onClose, onCreateApiKey }) => {
  const [newApiKey, setNewApiKey] = useState({ name: "", description: "" });
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    setNewApiKey({ name: "", description: "" });
    setCreatedKey(null);
    setCopied(false);
    onClose();
  };

  const handleCreate = async () => {
    const result = await onCreateApiKey(newApiKey.name, newApiKey.description);
    if (result) {
      setCreatedKey(result.key);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(createdKey);
    setCopied(true);
    toast.info("API Key copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const renderFormView = () => (
    <>
      <Typography
        component="h2"
        id="api-key-modal-title"
        level="h4"
        textColor="inherit"
        sx={{ fontWeight: "lg", mb: 1 }}
      >
        Create New API Key
      </Typography>
      <Typography
        id="api-key-modal-description"
        textColor="text.tertiary"
        sx={{ mb: 2 }}
      >
        Generate a long-living API key to access the system.
      </Typography>
      <FormControl sx={{ mb: 2 }}>
        <FormLabel>API Key Name</FormLabel>
        <Input
          fullWidth
          placeholder="Enter API Key name"
          value={newApiKey.name}
          onChange={(e) =>
            setNewApiKey({ ...newApiKey, name: e.target.value })
          }
        />
      </FormControl>
      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Description (Optional)</FormLabel>
        <Input
          fullWidth
          placeholder="Describe this API Key"
          value={newApiKey.description}
          onChange={(e) =>
            setNewApiKey({ ...newApiKey, description: e.target.value })
          }
        />
      </FormControl>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={handleClose} variant="plain">
          Cancel
        </Button>
        <Button onClick={handleCreate} variant="solid" color="primary">
          Create
        </Button>
      </Box>
    </>
  );

  const renderSuccessView = () => (
    <>
      <Typography
        component="h2"
        id="api-key-modal-title"
        level="h4"
        textColor="inherit"
        sx={{ fontWeight: "lg", mb: 1 }}
      >
        API Key Created
      </Typography>
      <Typography
        id="api-key-modal-description"
        textColor="text.tertiary"
        sx={{ mb: 2 }}
      >
        Copy your API key now. You won't be able to see it again.
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Input
          readOnly
          fullWidth
          value={createdKey}
          sx={{ fontFamily: "monospace", fontSize: "sm" }}
        />
        <IconButton
          onClick={handleCopy}
          variant="soft"
          color={copied ? "success" : "neutral"}
        >
          {copied ? <CheckIcon /> : <ContentCopyIcon />}
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={handleClose} variant="solid" color="primary">
          Done
        </Button>
      </Box>
    </>
  );

  return (
    <Modal
      aria-labelledby="api-key-modal-title"
      aria-describedby="api-key-modal-description"
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{ maxWidth: 500, borderRadius: "md", p: 3, boxShadow: "lg", bgcolor: "background.surface" }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} onClick={handleClose} />
        {createdKey ? renderSuccessView() : renderFormView()}
      </Sheet>
    </Modal>
  );
};

export default CreateApiKeyModal;
